import { z } from "@hono/zod-openapi";
import { accidentReportV1 } from "../../exception/accidentReport";
import { advanceRequestV1, advanceRequestV2 } from "../../request/advanceRequest";
import { dailyCheckCallV1 } from "../../checkIn/dailyCheckCall";
import { dropSwapArrivalV1 } from "../../checkIn/dropSwapArrival";
import { equipmentInventoryV1 } from "../../equipment/equipmentInventory";
import { fuelCardErrorV1 } from "../../exception/fuelCardError";
import { fuelRequestV1 } from "../../request/fuelRequest";
import { loadHomeRequestV1 } from "../../request/loadHomeRequest";
import { loadRefusedV1 } from "../../exception/loadRefused";
import { messageAcknowledgedV1 } from "../../acknowledgement/messageAcknowledged";
import { osdReportV1 } from "../../exception/osdReport";
import { otherV1 } from "../../uncategorized/other";
import { tollPassIssueV1 } from "../../exception/tollPassIssue";
import { trainerDailyFormV1 } from "../../training/trainerDailyForm";
import { trainingReportV1 } from "../../training/trainingReport";
import { verificationResponseV1 } from "../../acknowledgement/verificationResponse";
import type { TmsEventBodyOf, TmsEventKind } from "../../taxonomy";

/**
 * A macro the driver submitted from the cab.
 *
 * Forms are customer-configured, so which kind a submission maps to is a per-tenant vocabulary table in the
 * vendor adapter, with `other` as the fallback — a form nobody mapped still mints, labeled by its own title.
 *
 * The kinds it may state are declared here once, and the registry holds the subject to them.
 * `satisfies` rather than an annotation, so each name stays a literal and a typo is still a compile error.
 */
export const formSubmissionKinds = [
  "messageAcknowledged",
  "verificationResponse",
  "dailyCheckCall",
  "dropSwapArrival",
  "equipmentInventory",
  "accidentReport",
  "fuelCardError",
  "loadRefused",
  "osdReport",
  "tollPassIssue",
  "advanceRequest",
  "fuelRequest",
  "loadHomeRequest",
  "trainerDailyForm",
  "trainingReport",
  "other",
] as const satisfies readonly TmsEventKind[];

export type FormSubmissionKind = (typeof formSubmissionKinds)[number];

/**
 * The type of `tms_event.form_submission.facts`: any of this subject's kinds, at any version it declares.
 *
 * What the generated Drizzle column's `.$type<…>()` names, and what the repository parses every written and read
 * value against — so the column's compile-time type and its runtime contents answer to the same schema.
 *
 * Written out one arm at a time rather than derived from the kind list above. Every version a column may hold
 * is named here, so shipping a second version of a kind is a visible edit in this file rather than a silent
 * widening, and forgetting it fails the first time the writer emits that version. When a kind does reach two,
 * its arms nest in a union on `version`: zod keeps only the first arm when a discriminator value repeats, so
 * listing both flat would quietly parse one version's documents against the other's schema.
 */
export const FormSubmissionFactsSchema = z.discriminatedUnion("kind", [
  messageAcknowledgedV1.facts,
  verificationResponseV1.facts,
  dailyCheckCallV1.facts,
  dropSwapArrivalV1.facts,
  equipmentInventoryV1.facts,
  accidentReportV1.facts,
  fuelCardErrorV1.facts,
  loadRefusedV1.facts,
  osdReportV1.facts,
  tollPassIssueV1.facts,
  z.discriminatedUnion("version", [advanceRequestV1.facts, advanceRequestV2.facts]),
  fuelRequestV1.facts,
  loadHomeRequestV1.facts,
  trainerDailyFormV1.facts,
  trainingReportV1.facts,
  otherV1.facts,
]);

export type FormSubmissionFacts = z.infer<typeof FormSubmissionFactsSchema>;

/**
 * What a FormSubmission normalizer produces: references and facts, without the base the writer supplies.
 */
export type FormSubmissionBody = TmsEventBodyOf<FormSubmissionKind>;
